import { useEffect, useState } from 'react';
import { API_URL } from '../config';
import { Plus, Trash2, Edit, CreditCard, Building2, X } from 'lucide-react';
import DataTable from 'react-data-table-component';
import { useAuth } from '../context/AuthContext';

export default function CajasPage() {
    const { logout } = useAuth();
    const [cajas, setCajas] = useState([]);
    const [sucursales, setSucursales] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [formData, setFormData] = useState({ nombre: '', sucursal_id: '', tipo: '', estado_operativa: true });
    const [editingId, setEditingId] = useState(null);

    useEffect(() => {
        fetchData();
    }, []);

    const authHeader = () => ({ 'Authorization': `Bearer ${localStorage.getItem('token')}` });

    const fetchData = async () => {
        setLoading(true);
        try {
            const [cajasRes, sucursalesRes] = await Promise.all([
                fetch(`${API_URL}/api/cajas`, { headers: authHeader() }),
                fetch(`${API_URL}/api/sucursales`, { headers: authHeader() })
            ]);

            if (cajasRes.status === 401 || sucursalesRes.status === 401) {
                logout();
                return;
            }

            const cajasData = await cajasRes.json();
            const sucursalesData = await sucursalesRes.json();

            const cajasArray = Array.isArray(cajasData) ? cajasData : [];
            const sucursalesArray = Array.isArray(sucursalesData) ? sucursalesData : [];

            // Caja API already returns sucursal_nombre from JOIN; fallback to lookup if missing
            const cajasWithSucursal = cajasArray.map(caja => ({
                ...caja,
                sucursal_nombre: caja.sucursal_nombre ?? sucursalesArray.find(s => Number(s.id) === Number(caja.sucursal_id))?.nombre ?? '-'
            }));

            setCajas(cajasWithSucursal);
            setSucursales(sucursalesArray);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const url = editingId
            ? `${API_URL}/api/cajas/${editingId}`
            : `${API_URL}/api/cajas`;
        const method = editingId ? 'PUT' : 'POST';

        try {
            const res = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({
                    nombre: formData.nombre,
                    sucursal_id: formData.sucursal_id ? parseInt(formData.sucursal_id) : null,
                    tipo: formData.tipo ? parseInt(formData.tipo) : null,
                    estado: !!formData.estado_operativa
                })
            });
            if (res.ok) {
                setModalOpen(false);
                setFormData({ nombre: '', sucursal_id: '', tipo: '', estado_operativa: true });
                setEditingId(null);
                fetchData();
            }
        } catch (err) {
            console.error(err);
        }
    };

    const handleDelete = async (id) => {
        if (!confirm('¿Estás seguro de eliminar esta caja?')) return;
        try {
            await fetch(`${API_URL}/api/cajas/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            fetchData();
        } catch (err) {
            console.error(err);
        }
    };

    const openEdit = (caja) => {
        setFormData({ 
            nombre: caja.nombre,
            sucursal_id: caja.sucursal_id?.toString() || '',
            tipo: caja.tipo?.toString() || '',
            estado_operativa: caja.estado === true
        });
        setEditingId(caja.id);
        setModalOpen(true);
    };

    const openNew = () => {
        setEditingId(null);
        setFormData({ 
            nombre: '', 
            sucursal_id: sucursales[0]?.id?.toString() || '',
            tipo: '',
            estado_operativa: false
        });
        setModalOpen(true);
    };

    const columns = [
        { 
            name: 'ID', 
            selector: row => row.id, 
            sortable: true, 
            width: '80px' 
        },
        { 
            name: 'Nombre', 
            selector: row => row.nombre, 
            sortable: true,
            cell: row => (
                <div className="flex items-center gap-2">
                    <CreditCard size={16} className="text-green-600" />
                    <span className="font-medium">{row.nombre}</span>
                </div>
            )
        },
        { 
            name: 'Sucursal', 
            selector: row => row.sucursal_nombre, 
            sortable: true,
            cell: row => (
                <div className="flex items-center gap-2">
                    <Building2 size={14} className="text-indigo-500" />
                    <span>{row.sucursal_nombre}</span>
                </div>
            )
        },
        {
            name: 'Tipo',
            selector: row => row.tipo,
            sortable: true,
            cell: row => (
                <span className={`px-2 py-1 rounded text-xs ${
                    row.tipo === 1 ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                }`}>
                    {row.tipo === 1 ? 'Caja Central' : 'Punto de Venta'}
                </span>
            )
        },
        {
            name: 'Sesión POS',
            selector: row => row.sesion_abierta,
            cell: row => (
                <span className={`px-2 py-1 rounded text-xs ${
                    row.sesion_abierta ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                }`}>
                    {row.sesion_abierta ? 'Abierta' : 'Cerrada'}
                </span>
            )
        },
        {
            name: 'Operativa',
            selector: row => row.estado,
            sortable: true,
            cell: row => (
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                    row.estado === true ? 'bg-emerald-100 text-emerald-800' : 'bg-orange-100 text-orange-900'
                }`}>
                    {row.estado === true ? 'Sí' : 'No'}
                </span>
            ),
        },
        {
            name: 'Sucursal',
            selector: row => row.sucursal_estado,
            width: '120px',
            cell: row => (
                <span className={`px-2 py-1 rounded text-xs ${
                    row.sucursal_estado === false ? 'bg-red-100 text-red-800' : 'bg-slate-100 text-slate-700'
                }`}>
                    {row.sucursal_estado === false ? 'Suc. inactiva' : 'Sucursal OK'}
                </span>
            ),
        },
        {
            name: 'Acciones',
            cell: row => (
                <div className="flex gap-2">
                    <button onClick={() => openEdit(row)} className="text-blue-600 hover:text-blue-800 p-1">
                        <Edit size={18} />
                    </button>
                    <button onClick={() => handleDelete(row.id)} className="text-red-500 hover:text-red-700 p-1">
                        <Trash2 size={18} />
                    </button>
                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
        },
    ];

    return (
        <div className="p-6">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <CreditCard className="text-green-600" size={28} />
                        Gestión de Cajas
                    </h1>
                    <p className="text-gray-500 mt-1">
                        Administra las cajas registradoras de cada sucursal
                    </p>
                </div>
                <button onClick={openNew} className="btn-primary flex items-center gap-2">
                    <Plus size={18} /> Nueva Caja
                </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-white rounded-lg shadow-sm border p-4">
                    <div className="flex items-center gap-3">
                        <div className="p-3 bg-green-100 rounded-lg">
                            <CreditCard className="text-green-600" size={24} />
                        </div>
                        <div>
                            <p className="text-sm text-gray-500">Total Cajas</p>
                            <p className="text-2xl font-bold">{cajas.length}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-white rounded-lg shadow-sm border p-4">
                    <div className="flex items-center gap-3">
                        <div className="p-3 bg-blue-100 rounded-lg">
                            <CreditCard className="text-blue-600" size={24} />
                        </div>
                        <div>
                            <p className="text-sm text-gray-500">Puntos de Venta</p>
                            <p className="text-2xl font-bold">{cajas.filter(c => c.tipo !== 1).length}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-white rounded-lg shadow-sm border p-4">
                    <div className="flex items-center gap-3">
                        <div className="p-3 bg-purple-100 rounded-lg">
                            <CreditCard className="text-purple-600" size={24} />
                        </div>
                        <div>
                            <p className="text-sm text-gray-500">Cajas Centrales</p>
                            <p className="text-2xl font-bold">{cajas.filter(c => c.tipo === 1).length}</p>
                        </div>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">Cargando...</div>
            ) : (
                <div className="bg-white rounded-lg shadow overflow-hidden">
                    <DataTable
                        columns={columns}
                        data={cajas}
                        pagination
                        highlightOnHover
                        pointerOnHover
                        responsive
                        noDataComponent={
                            <div className="p-8 text-center text-gray-500">
                                No hay cajas registradas
                            </div>
                        }
                        customStyles={{
                            headRow: {
                                style: {
                                    backgroundColor: '#f9fafb',
                                    borderBottom: '1px solid #e5e7eb',
                                    fontWeight: '600',
                                    color: '#4b5563',
                                },
                            },
                        }}
                    />
                </div>
            )}

            {modalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold">
                                {editingId ? 'Editar Caja' : 'Nueva Caja'}
                            </h2>
                            <button onClick={() => setModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                                <X size={24} />
                            </button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-1">Sucursal *</label>
                                <select
                                    className="w-full p-2 border rounded-lg"
                                    required
                                    value={formData.sucursal_id}
                                    onChange={e => setFormData({ ...formData, sucursal_id: e.target.value })}
                                >
                                    <option value="">Seleccionar sucursal...</option>
                                    {sucursales.map(suc => (
                                        <option key={suc.id} value={suc.id}>{suc.nombre}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-1">Nombre *</label>
                                <input
                                    className="w-full p-2 border rounded-lg"
                                    required
                                    value={formData.nombre}
                                    onChange={e => setFormData({ ...formData, nombre: e.target.value })}
                                    placeholder="Ej. Caja 1"
                                />
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-1">Tipo</label>
                                <select
                                    className="w-full p-2 border rounded-lg"
                                    value={formData.tipo}
                                    onChange={e => setFormData({ ...formData, tipo: e.target.value })}
                                >
                                    <option value="">Punto de Venta</option>
                                    <option value="1">Caja Central</option>
                                </select>
                            </div>
                            <div className="mb-4">
                                <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={formData.estado_operativa === true}
                                        onChange={e =>
                                            setFormData({ ...formData, estado_operativa: e.target.checked })
                                        }
                                    />
                                    Caja operativa para ventas (POS)
                                </label>
                                <p className="text-xs text-gray-500 mt-1">Requiere además sucursal activa.</p>
                            </div>
                            <div className="flex gap-3 mt-6">
                                <button
                                    type="button"
                                    onClick={() => setModalOpen(false)}
                                    className="flex-1 py-2 border rounded-lg hover:bg-gray-50"
                                >
                                    Cancelar
                                </button>
                                <button type="submit" className="flex-1 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
                                    {editingId ? 'Guardar Cambios' : 'Crear Caja'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
